import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Col, Row, Form, Button, Skeleton, message, Modal } from "antd";
import {
  CheckOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";

import ClickableTextButton from "components/ClickableTextButton/ClickableTextButton";
import FormInputNumber from "components/FormInputNumber/FormInputNumber";
import ScrollableContainer from "components/ScrollableContainer/ScrollableContainer";
import SectionCard from "components/SectionCard/SectionCard";
import Title from "components/Title/Title";

import { buildManageEmergencyContactTemplateUri } from "utils/routes";

import { useFetchConstant } from "apis/constant";
import { useGetValues, patchValues, refetchValues } from "apis/valueStore";
import { withAppContext } from "contexts/AppContext/AppContext";
import { getLabelOfConstant } from "utils/general";
import { patchUpdateUser } from "apis/user";

import { SectionContainer } from "./Settings.styles";
import Locale from "react-intl-universal";

const { useForm } = Form;
const { success, error, confirm } = Modal;

const Settings = ({ isAdmin, getLoggedInUser }) => {
  const history = useHistory();
  const [form] = useForm();
  const [loading, setLoading] = useState(false);

  const loggedInUser = getLoggedInUser();
  const { isLoading: isValuesLoading, data } = useGetValues();
  const {
    isLoading: isGlobalConfigLoading,
    selection: globalConfigs,
  } = useFetchConstant("globalConfigs");

  const isLoading = isValuesLoading || isGlobalConfigLoading;

  const onBackButtonClick = () => history.goBack();

  const processInitialValues = () => {
    if (history.location.pathname === "/settings") {
      if (loggedInUser.settings) {
        // Set user rate
        form.setFieldsValue({
          settings: {
            inflation: loggedInUser.settings.inflation * 100,
            fd: loggedInUser.settings.fd * 100,
            investment: loggedInUser.settings.investment * 100,
          },
        });
      } else {
        // Set default value
        form.setFieldsValue({
          settings: {
            inflation:
              data.filter((x) => x.name === "INFLATION_RATE")[0].value * 100,
            fd: data.filter((x) => x.name === "FD_RATE")[0].value * 100,
            investment:
              data.filter((x) => x.name === "INVESTMENT_INTEREST_RATE")[0]
                .value * 100,
          },
        });
      }

      return data.map((data, idx) => ({
        ...data,
        value: data.value * 100,
      }));
    } else if (history.location.pathname === "/global-settings") {
      return data.map((data, idx) => ({
        ...data,
        value: data.value * 100,
      }));
    }
  };

  const handleOnSaveButtonClick = async (e) => {
    if (history.location.pathname === "/settings") {
      // User Settings
      e.preventDefault();
      try {
        const values = await form.validateFields();
        console.log(values, 76);

        values.settings.fd = values.settings.fd / 100;
        values.settings.inflation = values.settings.inflation / 100;
        values.settings.investment = values.settings.investment / 100;

        confirm({
          title: Locale.get("Settings.confirmSaveTitle"),
          content: Locale.get("Settings.confirmSaveContent"),
          icon: <ExclamationCircleOutlined />,
          onOk() {
            patchUpdateUser(loggedInUser._id, values)
              .then(() => {
                // refetchValues();
                success({
                  title: Locale.get("Settings.confirmUpdate"),
                  onOk() {
                    window.location.reload();
                  },
                });
              })
              .catch((ex) => {
                error({
                  title: ex.message,
                });
              });
          },
          onCancel() {},
        });
      } catch (error) {
        error.errorFields.forEach((field) => message.error(field.errors[0]));
      }
    } else if (history.location.pathname === "/global-settings") {
      // Global Settings
      e.preventDefault();
      try {
        const values = await form.validateFields();
        const payload = data.map((data, idx) => ({
          ...data,
          value: values[idx].value / 100,
        }));

        confirm({
          title: Locale.get("Settings.confirmSaveTitle"),
          content: Locale.get("Settings.confirmSaveContent"),
          icon: <ExclamationCircleOutlined />,
          onOk() {
            patchValues(payload)
              .then(() => {
                // refetchValues();
                success({
                  title: Locale.get("Settings.confirmUpdate"),
                  onOk() {
                    window.location.reload();
                  },
                });
              })
              .catch((ex) => {
                error({
                  title: ex.message,
                });
              });
          },
          onCancel() {},
        });
      } catch (error) {
        error.errorFields.forEach((field) => message.error(field.errors[0]));
      }
    }
  };

  return (
    <>
      {isLoading ? (
        <Skeleton active />
      ) : (
        <>
          <Form form={form} initialValues={processInitialValues()}>
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: "32px" }}
            >
              <ClickableTextButton
                text={Locale.get("Settings.backButton")}
                onClick={onBackButtonClick}
                showIcon={true}
                type="back"
              />
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleOnSaveButtonClick}
              >
                {Locale.get("Settings.saveButtonTitle")}
              </Button>
            </Row>
            <ScrollableContainer>
              <Row align="middle" style={{ marginBottom: 32 }}>
                <Title /* marginBottom="32px" */>
                  {Locale.get("Settings.pageTitle")}
                </Title>
              </Row>

              <SectionContainer>
                <SectionCard title={Locale.get("Settings.rateSettingTitle")}>
                  <Row gutter={16}>
                    {data.map((data, idx) => {
                      if (
                        history.location.pathname === "/settings"
                        //  &&
                        // data.name !== "INVESTMENT_INTEREST_RATE"
                      ) {
                        // Settings
                        return (
                          <Col span={12}>
                            <FormInputNumber
                              name={
                                data.name === "FD_RATE"
                                  ? ["settings", "fd"]
                                  : data.name === "INFLATION_RATE"
                                  ? ["settings", "inflation"]
                                  : ["settings", "investment"]
                              }
                              label={
                                data.name === "FD_RATE"
                                  ? Locale.get("Settings.fdRate")
                                  : data.name === "INFLATION_RATE"
                                  ? Locale.get("Settings.inflationRate")
                                  : Locale.get("Settings.interestRate")
                              }
                              // label={getLabelOfConstant(
                              //   data.name,
                              //   globalConfigs,
                              //   "name"
                              // )}
                              requiredErrorMessage="Please enter a number."
                              formatter={(value) => `${value}%`}
                              parser={(value) => value.replace(/[^0-9.]/g, "")}
                              precision={2}
                              step={0.01}
                              min={0.01}
                            />
                          </Col>
                        );
                      } else if (
                        history.location.pathname === "/global-settings"
                      ) {
                        // Global Settings
                        return (
                          <Col span={12}>
                            <FormInputNumber
                              name={[idx, "value"]}
                              label={
                                data.name === "FD_RATE"
                                  ? Locale.get("Settings.fdRate")
                                  : data.name === "INFLATION_RATE"
                                  ? Locale.get("Settings.inflationRate")
                                  : Locale.get("Settings.interestRate")
                              }
                              // {getLabelOfConstant(
                              //   data.name,
                              //   globalConfigs,
                              //   "name"
                              // )}
                              requiredErrorMessage="Please enter a number."
                              formatter={(value) => `${value}%`}
                              parser={(value) => value.replace(/[^0-9.]/g, "")}
                              precision={2}
                              step={0.01}
                              min={0.01}
                            />
                          </Col>
                        );
                      }
                    })}
                  </Row>
                </SectionCard>
              </SectionContainer>
            </ScrollableContainer>
          </Form>
        </>
      )}
    </>
  );
};

export default withAppContext(Settings);
